/* eslint-disable no-console */
import {Platform} from 'react-native';
import {check, PERMISSIONS, request, RESULTS} from 'react-native-permissions';

import {APP_FIRST_LAUNCH} from '/enum/storage.enum';
import {getOneTrustUtilDefaultInstance} from '/util/oneTrust';

import {createStorage} from './async-storage';

export const TRACKING_STATUSES = {
  UNAVAILABLE: 'unavailable', // Pre IOS 14 or Android
  DENIED: 'denied', // User selected Do Not Track
  AUTHORIZED: 'authorized', // User granted permission to Track
  RESTRICTED: 'restricted', // Do Not Track selected and User does not have permission to change it
  NOT_DETERMINED: 'not-determined', // User has not been promted for Tracking permission
};

function mapPermissionStatus(result: string) {
  switch (result) {
    case RESULTS.GRANTED:
      return TRACKING_STATUSES.AUTHORIZED;
    case RESULTS.DENIED:
      return TRACKING_STATUSES.DENIED;
    case RESULTS.BLOCKED:
      return TRACKING_STATUSES.RESTRICTED;
    case RESULTS.UNAVAILABLE:
      return TRACKING_STATUSES.UNAVAILABLE;
    default:
      return TRACKING_STATUSES.NOT_DETERMINED;
  }
}

/**
 * enableTracking - Enables tracking in Tealium, Firebase, Fullstory
 *
 * @param {object} params - Parameters
 * @param {Boolean} params.trackingStatus - One of TRACKING_STATUSES
 * @returns {Boolean} Whether the tracking was enabled in the analytics
 */
export function enableTracking({trackingStatus}: any) {
  const status =
    trackingStatus === TRACKING_STATUSES.AUTHORIZED ||
    trackingStatus === TRACKING_STATUSES.UNAVAILABLE ||
    trackingStatus === TRACKING_STATUSES.NOT_DETERMINED;
  if (status) {
    getOneTrustUtilDefaultInstance().setATTConsent(true);
    return true;
  }
  getOneTrustUtilDefaultInstance().setATTConsent(false);
  return false;
}

/**
 * setupATT - A function to Enable tracking if has permissions or requests tracking permission if it doesn't
 */
export async function setupATT() {
  try {
    if (Platform.OS !== 'ios') {
      getOneTrustUtilDefaultInstance().setATTConsent(true);
      getOneTrustUtilDefaultInstance().initialize();
      return;
    }

    let result = await check(PERMISSIONS.IOS.APP_TRACKING_TRANSPARENCY);
    let trackingStatus = mapPermissionStatus(result);
    if (trackingStatus === TRACKING_STATUSES.NOT_DETERMINED) {
      const {setValue: setAppFirstLaunch} = createStorage({
        key: APP_FIRST_LAUNCH,
        defaultValue: null,
      });
      setAppFirstLaunch(true);
    } else if (trackingStatus === TRACKING_STATUSES.AUTHORIZED) {
      const {setValue: setAppFirstLaunch} = createStorage({
        key: APP_FIRST_LAUNCH,
        defaultValue: null,
      });
      setAppFirstLaunch(false);
    }

    const wasEnabled = enableTracking({trackingStatus});
    if (!wasEnabled || trackingStatus === TRACKING_STATUSES.NOT_DETERMINED) {
      const requestResult = await request(
        PERMISSIONS.IOS.APP_TRACKING_TRANSPARENCY,
      );
      const updatedStatus = mapPermissionStatus(requestResult);
      const isEnabled = enableTracking({trackingStatus: updatedStatus});

      if (!isEnabled) {
        getOneTrustUtilDefaultInstance().setATTConsent(false);
      }
    }

    getOneTrustUtilDefaultInstance().initialize();
  } catch (error) {
    console.error('ATT Setup Failed:', error);
  }
}
